<<template>
    <div>
        <v-toolbar dense tabs color="primary darken-2" dark>
            <v-toolbar-title class="pl-2">
                <a href="javascript: history.go(-1)" class="pa-0">
                    <v-btn flat fab small class="pa-0">
                        <i class="fas fa-arrow-left headline"></i>
                    </v-btn>
                </a>
                All Investments
            </v-toolbar-title>
        </v-toolbar>

        <div class="text-xs-right">
            <v-layout justify-end align-center class="button-group">
                <div class="mr-2">
                    <v-btn
                        class="white--text elevation-4"
                        small
                        rounded
                        color="purple darken-2"
                        :to="{name: 'investments.create'}"
                        style="min-width: 160px; border-radius: 24px;"
                    >
                        <v-icon left small class="fas fa-chart-line"></v-icon>
                        <span>New Investment</span>
                    </v-btn>
                </div>
            </v-layout>
        </div>

        <!-- No investments message -->
        <div v-if="investments.length === 0 && !isLoading">
            <v-card class="elevation-4 mt-4 pa-5 text-xs-center" style="border-radius: 10px">
                <v-icon size="64" color="grey lighten-1">fas fa-chart-line</v-icon>
                <h2 class="grey--text mt-3">No investments yet</h2>
                <p class="grey--text">Start investing today and earn 18% interest!</p>

                <!-- Bouton Create Investment -->
                <v-btn
                    color="purple darken-2"
                    class="white--text mt-3"
                    :to="{name: 'investments.create'}"
                >
                    <v-icon left>fas fa-plus</v-icon>
                    Create Investment
                </v-btn>

                <!-- 🔥 BOUTON LEARN MORE -->
                <v-btn
                    color="teal darken-2"
                    flat
                    class="mt-3 ml-2"
                    @click="showLearnMoreDialog = true"
                >
                    <v-icon left small>fas fa-info-circle</v-icon>
                    Learn More
                </v-btn>
            </v-card>
        </div>

        <!-- Investments List -->
        <div class="mt-3 pa-0" style="max-height: 500px; overflow: auto">
            <v-hover v-for="item in investments" :key="item.id">
                <v-card
                    slot-scope="{ hover }"
                    :class="`elevation-${hover ? 18 : 4}`"
                    color="white"
                    class="mb-3"
                    style="border-radius: 10px; cursor: pointer"
                    :to="'/savings/investments/detail/'+item.id"
                >
                    <v-card-text class="px-3 py-3">
                        <v-layout row wrap align-center>
                            <v-flex xs12 sm6 md4>
                                <v-layout align-center>
                                    <v-avatar :color="getStatusColor(item.status)" size="48" class="mr-3">
                                        <v-icon dark>fas fa-chart-line</v-icon>
                                    </v-avatar>
                                    <div>
                                        <h3 :class="getStatusTextColor(item.status)">
                                            {{ formatAmount(item.investment_amount) }} FCFA
                                        </h3>
                                        <p class="caption mb-0 grey--text">
                                            Investment #{{ item.id }}
                                        </p>
                                    </div>
                                </v-layout>
                            </v-flex>

                            <v-flex xs12 sm6 md3 class="text-xs-center">
                                <v-chip
                                    small
                                    :color="getStatusColor(item.status)"
                                    dark
                                    class="mb-1"
                                >
                                    <v-icon x-small left>{{ getStatusIcon(item.status) }}</v-icon>
                                    {{ getStatusLabel(item.status) }}
                                </v-chip>
                                <br>
                                <v-chip
                                    x-small
                                    :color="item.payment_type === 'monthly' ? 'blue' : 'purple'"
                                    dark
                                >
                                    {{ item.payment_type === 'monthly' ? 'Monthly' : 'One-Time' }}
                                </v-chip>
                            </v-flex>

                            <v-flex xs12 sm6 md3>
                                <p class="caption mb-1 grey--text">Progress</p>
                                <v-progress-linear
                                    :value="item.progress"
                                    height="20"
                                    :color="getProgressColor(item.progress)"
                                    striped
                                >
                                    <strong class="white--text">{{ item.progress }}%</strong>
                                </v-progress-linear>
                            </v-flex>

                            <v-flex xs12 sm6 md2 class="text-xs-right">
                                <p class="caption mb-1 grey--text">Interest Earned</p>
                                <h4 class="green--text text--darken-2">
                                    +{{ formatAmount(item.interest_earned) }} FCFA
                                </h4>
                            </v-flex>
                        </v-layout>

                        <v-divider class="my-2"></v-divider>
                        <v-layout row wrap>
                            <v-flex xs6 sm3>
                                <p class="caption mb-0 grey--text">Start Date</p>
                                <p class="body-2 mb-0">{{ formatDate(item.start_date) }}</p>
                            </v-flex>
                            <v-flex xs6 sm3>
                                <p class="caption mb-0 grey--text">End Date</p>
                                <p class="body-2 mb-0">{{ formatDate(item.end_date) }}</p>
                            </v-flex>
                            <v-flex xs6 sm3>
                                <p class="caption mb-0 grey--text">Total Paid</p>
                                <p class="body-2 mb-0 teal--text">{{ formatAmount(item.total_paid) }} FCFA</p>
                            </v-flex>
                            <v-flex xs6 sm3>
                                <p class="caption mb-0 grey--text">Created</p>
                                <p class="body-2 mb-0">{{ formatDate(item.creation_date) }}</p>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-card>
            </v-hover>
        </div>

        <!-- 🔥 DIALOG LEARN MORE -->
        <v-dialog v-model="showLearnMoreDialog" max-width="800px" scrollable>
            <v-card style="border-radius: 16px; overflow: hidden;">
                <!-- Header -->
                <v-card-title
                    class="white--text py-3"
                    style="background: linear-gradient(135deg, #6A1B9A 0%, #4A148C 100%);"
                >
                    <v-icon left color="white" size="28">fas fa-lightbulb</v-icon>
                    <span class="headline font-weight-bold">PettyCash SAS Term Investment</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="showLearnMoreDialog = false">
                        <v-icon>fas fa-times</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-4" style="max-height: 600px;">
                    <!-- Intro -->
                    <v-alert
                        type="info"
                        border="left"
                        colored-border
                        elevation="2"
                        class="mb-4"
                        icon="fas fa-chart-line"
                    >
                        <div class="subtitle-1 font-weight-bold mb-1">Invest in Our Growth</div>
                        <p class="body-2 mb-0">
                            Invest in the growth of <strong>PettyCash SAS</strong> and earn attractive annual returns while supporting the expansion of our innovative Fintech company.
                        </p>
                    </v-alert>

                    <!-- Investment Overview -->
                    <div class="mb-4">
                        <h3 class="purple--text text--darken-2 mb-2">
                            <v-icon small color="purple darken-2" class="mr-1">fas fa-bullseye</v-icon>
                            Investment Overview
                        </h3>
                        <p class="body-2 grey--text text--darken-2">
                            PettyCash SAS offers a <strong>1-Year Term Investment Program</strong> that allows individuals and businesses to invest in the company and receive a <strong>fixed annual return of 18%</strong> upon maturity.
                        </p>
                    </div>

                    <!-- Investment Details -->
                    <div class="mb-4">
                        <h3 class="purple--text text--darken-2 mb-3">
                            <v-icon small color="purple darken-2" class="mr-1">fas fa-list-alt</v-icon>
                            Investment Details
                        </h3>
                        <v-layout row wrap>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-card class="elevation-2 pa-3" style="border-radius: 10px; border-left: 4px solid #6A1B9A;">
                                    <v-layout align-center class="mb-2">
                                        <v-icon color="purple darken-2" class="mr-2">fas fa-calendar-alt</v-icon>
                                        <h4 class="purple--text text--darken-2 mb-0">Duration</h4>
                                    </v-layout>
                                    <p class="body-2 grey--text text--darken-2 mb-0">12 Months fixed term</p>
                                </v-card>
                            </v-flex>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-card class="elevation-2 pa-3" style="border-radius: 10px; border-left: 4px solid #4CAF50;">
                                    <v-layout align-center class="mb-2">
                                        <v-icon color="green darken-2" class="mr-2">fas fa-percentage</v-icon>
                                        <h4 class="green--text text--darken-2 mb-0">Annual Return</h4>
                                    </v-layout>
                                    <p class="body-2 grey--text text--darken-2 mb-0"><strong>18%</strong> fixed at maturity</p>
                                </v-card>
                            </v-flex>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-card class="elevation-2 pa-3" style="border-radius: 10px; border-left: 4px solid #2196F3;">
                                    <v-layout align-center class="mb-2">
                                        <v-icon color="blue darken-2" class="mr-2">fas fa-arrow-down</v-icon>
                                        <h4 class="blue--text text--darken-2 mb-0">Minimum</h4>
                                    </v-layout>
                                    <p class="body-2 grey--text text--darken-2 mb-0"><strong>250,000 FCFA</strong></p>
                                </v-card>
                            </v-flex>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-card class="elevation-2 pa-3" style="border-radius: 10px; border-left: 4px solid #FF9800;">
                                    <v-layout align-center class="mb-2">
                                        <v-icon color="orange darken-2" class="mr-2">fas fa-arrow-up</v-icon>
                                        <h4 class="orange--text text--darken-2 mb-0">Maximum</h4>
                                    </v-layout>
                                    <p class="body-2 grey--text text--darken-2 mb-0"><strong>3,000,000 FCFA</strong></p>
                                </v-card>
                            </v-flex>
                        </v-layout>
                        <v-alert type="success" border="left" colored-border class="mt-3 mb-0" icon="fas fa-credit-card">
                            <p class="body-2 mb-0">
                                <strong>Payment Options:</strong> One-time payment or flexible installment contributions
                            </p>
                        </v-alert>
                    </div>

                    <!-- Why Invest -->
                    <div class="mb-4">
                        <h3 class="purple--text text--darken-2 mb-2">
                            <v-icon small color="purple darken-2" class="mr-1">fas fa-hand-holding-heart</v-icon>
                            Why Invest with PettyCash SAS?
                        </h3>
                        <p class="body-2 grey--text text--darken-2 mb-2">
                            Your investment will contribute directly to:
                        </p>
                        <v-list dense class="elevation-1" style="border-radius: 8px;">
                            <v-list-tile v-for="(reason, index) in investReasons" :key="index">
                                <v-list-tile-avatar>
                                    <v-icon color="purple" small>fas fa-check-circle</v-icon>
                                </v-list-tile-avatar>
                                <v-list-tile-content>
                                    <v-list-tile-title class="body-2">{{ reason }}</v-list-tile-title>
                                </v-list-tile-content>
                            </v-list-tile>
                        </v-list>
                    </div>

                    <!-- About PettyCash SAS -->
                    <div class="mb-4">
                        <h3 class="purple--text text--darken-2 mb-2">
                            <v-icon small color="purple darken-2" class="mr-1">fas fa-building</v-icon>
                            About PettyCash SAS
                        </h3>
                        <v-card class="elevation-2 pa-3" style="border-radius: 10px;">
                            <p class="body-2 grey--text text--darken-2 mb-2">
                                PettyCash SAS is a <strong>registered fintech company</strong> committed to providing innovative digital financial solutions.
                            </p>
                            <v-alert type="info" border="left" colored-border class="mb-0" icon="fas fa-map-marker-alt">
                                <p class="body-2 mb-0">
                                    <strong>Head Office:</strong> Polytechnique Incubation and Acceleration Programme
                                </p>
                            </v-alert>
                        </v-card>
                    </div>

                    <!-- Important Information -->
                    <div class="mb-2">

                        <v-alert type="warning" border="left" colored-border class="mb-2">
                            <p class="body-2 mb-2">
                                Investments are subject to the terms and conditions of the <strong>PettyCash SAS Term Investment Program</strong>.
                            </p>
                            <p class="body-2 mb-0 font-italic">
                                Join us in building the future of digital finance while earning competitive returns on your investment.
                            </p>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="pa-4" style="background: #f5f5f5;">
                    <v-btn
                        color="purple darken-2"
                        class="white--text"
                        style="border-radius: 24px;"
                        :to="{name: 'investments.create'}"
                        @click="showLearnMoreDialog = false"
                    >
                        <v-icon left small>fas fa-plus</v-icon>
                        Start Investing Now
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="grey darken-1" flat @click="showLearnMoreDialog = false">
                        <v-icon left small>fas fa-check</v-icon>
                        Got it
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            investments: [],
            category: 'all',
            isLoading: false,
            showLearnMoreDialog: false, // 🔥 État du dialog

            // 🔥 Pages disponibles pour la navigation
            investmentPages: [
                {
                    title: 'Create Investment',
                    description: 'Start a new one-time or installment investment (250K - 3M FCFA)',
                    icon: 'fas fa-plus-circle',
                    color: 'purple',
                    link: '/savings/investments/create'
                },
                {
                    title: 'My Investments',
                    description: 'View all your active, completed and pending investments',
                    icon: 'fas fa-chart-line',
                    color: 'blue',
                    link: '/savings/investments'
                },
                {
                    title: 'All PettyCash Investments',
                    description: 'Explore all investments across the platform in real time',
                    icon: 'fas fa-chart-pie',
                    color: 'indigo',
                    link: '/savings/investors'
                },
                {
                    title: 'Investment Details',
                    description: 'Track payments, progress, interest earned and due dates',
                    icon: 'fas fa-eye',
                    color: 'teal',
                    link: '/savings/investments'
                }
            ],

            // 🔥 Raisons d'investir
            investReasons: [
                'Expansion of PettyCash services and operations',
                'Development of new financial products and technologies',
                'Business growth and market expansion',
                'Achievement of key company milestones',
                'Strengthening of our fintech infrastructure'
            ]
        }
    },

    methods: {
        formatAmount(value) {
            if (!value) return '0'
            return new Intl.NumberFormat('fr-FR').format(value)
        },

        formatDate(value) {
            if (!value) return '-'
            return new Date(value).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },

        getStatusColor(status) {
            const colors = {
                'active': 'green',
                'completed': 'blue',
                'cancelled': 'red',
                'pending_first_payment': 'orange'
            }
            return colors[status] || 'grey'
        },

        getStatusTextColor(status) {
            const colors = {
                'active': 'green--text text--darken-2',
                'completed': 'blue--text text--darken-2',
                'cancelled': 'red--text text--darken-2',
                'pending_first_payment': 'orange--text text--darken-2'
            }
            return colors[status] || 'grey--text'
        },

        getStatusLabel(status) {
            const labels = {
                'active': 'Active',
                'completed': 'Completed',
                'cancelled': 'Cancelled',
                'pending_first_payment': 'Pending'
            }
            return labels[status] || status
        },

        getStatusIcon(status) {
            const icons = {
                'active': 'fas fa-check-circle',
                'completed': 'fas fa-trophy',
                'cancelled': 'fas fa-times-circle',
                'pending_first_payment': 'fas fa-clock'
            }
            return icons[status] || 'fas fa-question-circle'
        },

        getProgressColor(progress) {
            if (progress >= 100) return 'green'
            if (progress >= 50) return 'blue'
            if (progress >= 25) return 'orange'
            return 'red'
        },

        async getInvestments() {
            this.isLoading = true
            this.$store.commit('setIsLoading', true)

            try {
                const response = await axios.get('/api/v1/savings/investments/list/')
                this.investments = response.data.investments || []

                const category = this.$route.params.category
                if (category && category !== 'all') {
                    this.investments = this.investments.filter(inv => inv.status === category)
                }
                this.category = category || 'all'

            } catch (error) {
                if (error.response) {
                    console.log(error.response)
                    this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                    this.$store.commit('setSnackBarColor', "red darken-4")
                    this.$store.commit('activateSnackBar', true)
                } else {
                    this.$store.commit('setSnackBarMessage', "An error occurred, please check your internet connection and try again later.")
                    this.$store.commit('setSnackBarColor', "red darken-4")
                    this.$store.commit('activateSnackBar', true)
                }
            } finally {
                this.isLoading = false
                this.$store.commit('setIsLoading', false)
            }
        },
    },

    watch: {
        '$route': function(to, from) {
            this.getInvestments()
        }
    },

    mounted() {
        this.getInvestments()
        const category = this.$route.params.category || 'all'
        document.title = "PettyCash | " + category + " Investments"
        sessionStorage.setItem('url', window.location.href)
        this.$store.commit('setCloseURL', "/investments")
    },
}
</script>

<style scoped>
.button-group {
    gap: 8px;
    margin-bottom: 16px;
}

.v-card {
    transition: all 0.3s ease;
}

.v-card:hover {
    transform: translateY(-2px);
}
</style>